// Pure geometry for the buyer-location radar. No DOM access so it can be unit
// tested directly. Distances/offsets use an equirectangular approximation
// centered on the venue, which is plenty accurate at metro-area scale.

export const RADAR_SIZE = 260;
const MILES_PER_DEGREE = 69;
const PADDING_PX = 18; // keep dots/labels off the edge

const toRad = (deg) => (deg * Math.PI) / 180;

// Great-circle distance in miles (haversine).
export const milesBetween = (aLat, aLng, bLat, bLng) => {
  const R = 3958.8;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const buildRadar = ({ venue, cities, radiusMi, size = RADAR_SIZE }) => {
  const center = size / 2;
  const usableRadius = center - PADDING_PX;
  const vLatRad = toRad(venue.latitude);

  const raw = cities.map((c) => {
    const dxMiles = (c.longitude - venue.longitude) * MILES_PER_DEGREE * Math.cos(vLatRad);
    const dyMiles = (c.latitude - venue.latitude) * MILES_PER_DEGREE;
    return { c, dxMiles, dyMiles, dist: Math.hypot(dxMiles, dyMiles) };
  });

  const farthest = raw.reduce((m, r) => Math.max(m, r.dist), 0);
  // Show at least 1.4x the radius ring; expand to fit outliers up to 3x radius.
  const viewMaxMiles = Math.max(radiusMi * 1.4, Math.min(farthest, radiusMi * 3)) || radiusMi;
  const pxPerMile = usableRadius / viewMaxMiles;
  const maxTickets = cities.reduce((m, c) => Math.max(m, c.tickets), 1);

  const points = raw.map(({ c, dxMiles, dyMiles }) => {
    let x = center + dxMiles * pxPerMile;
    let y = center - dyMiles * pxPerMile; // invert for screen coordinates
    const ox = x - center;
    const oy = y - center;
    const rPx = Math.hypot(ox, oy);
    if (rPx > usableRadius) {
      const k = usableRadius / rPx;
      x = center + ox * k;
      y = center + oy * k;
    }
    const r = 4 + Math.sqrt(c.tickets / maxTickets) * 12; // 4..16 px
    return {
      city: c.city,
      state: c.state,
      tickets: c.tickets,
      within_radius: c.within_radius,
      x,
      y,
      r,
    };
  });

  const ringPx = Math.min(radiusMi * pxPerMile, usableRadius);

  return { size, center, usableRadius, pxPerMile, viewMaxMiles, ringPx, points };
};
